import { Entity, MilestoneEntity, PartialEntity } from '@/common/types/entities';
import {
  LocalProjectChangeEvent,
  LocalProjectChangeEventTemplate,
  OperationInputType,
  RemoteProjectChangeEvent,
} from '@/features/realTimeCollaboration/types';
import { getEventCreationInformation } from '@/features/realTimeCollaboration/utils';

import { MilestoneType } from '../types';

export function convertMilestoneCreateOperationInput(
  input: OperationInputType<'CreateMilestones'>,
): MilestoneEntity[] {
  return input.map((m) => ({
    ...m,
    date: new SchedulingDate(m.date),
    wbsSection: m.wbsSectionId ? { id: m.wbsSectionId } : null,
    isFixed: m.type === MilestoneType.FIXED,
    type: m.type as MilestoneType,
    acceptanceCriteria: m.criteria ?? [],
    completedAt: null,
    tenantTradeVariation: m.tradeId ? { id: m.tradeId } : null,
    contributorGroup: m.contributorGroupId ? { id: m.contributorGroupId } : null,
  }));
}

export function convertMilestoneUpdateOperationInput(
  event: LocalProjectChangeEvent | RemoteProjectChangeEvent | LocalProjectChangeEventTemplate,
  input: (OperationInputType<'UpdateMilestones'>[0] &
    Partial<OperationInputType<'UpdateMilestoneStatus'>>)[],
): PartialEntity<MilestoneEntity>[] {
  const { createdBy } = getEventCreationInformation(event);

  return input.map((m) => ({
    id: m.id,
    ...(m.color !== undefined ? { color: m.color ?? '' } : {}),
    ...(m.date != undefined ? { date: new SchedulingDate(m.date) } : {}),
    ...(m.name !== undefined ? { name: m.name ?? '' } : {}),
    ...(m.wbsSectionId !== undefined
      ? { wbsSection: typeof m.wbsSectionId === 'string' ? { id: m.wbsSectionId } : null }
      : {}),
    ...(m.type !== undefined
      ? { isFixed: m.type === MilestoneType.FIXED, type: m.type as MilestoneType }
      : {}),
    ...(m.criteria !== undefined ? { acceptanceCriteria: m.criteria ?? [] } : {}),
    ...(m.completedAt !== undefined
      ? { completedAt: typeof m.completedAt === 'string' ? new Date(m.completedAt) : null }
      : {}),
    ...(m.completedAt !== undefined
      ? { completedBy: typeof m.completedAt === 'string' ? createdBy : null }
      : {}),
    ...(m.tradeId !== undefined
      ? { tenantTradeVariation: m.tradeId ? { id: m.tradeId } : null }
      : {}),
    ...(m.contributorGroupId !== undefined
      ? { contributorGroup: m.contributorGroupId ? { id: m.contributorGroupId } : null }
      : {}),
  }));
}

export function convertMilestoneDeleteOperationInput(
  input: OperationInputType<'RemoveScheduleNodes'>,
): Entity[] {
  return input.milestones?.map((id) => ({ id })) ?? [];
}

export function getOverlappingMilestonesByDateAndResource(milestones: MilestoneEntity[]) {
  return milestones.reduce<Record<string, number>>((acc, milestone) => {
    const key = generateDateIdKey(milestone.date, milestone.wbsSection?.id ?? '');
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});
}

export function getResourcesWithOverlappingMilestones(
  overlappingMilestones: Record<string, number>,
): Set<string> {
  const ids = new Set<string>();
  for (const [date, count] of Object.entries(overlappingMilestones)) {
    if (count === 0) continue;

    // This assumes generateDateIdKey separates with '_'
    const id = date.split('_', 1)[0];

    if (id) {
      ids.add(id);
    }
  }
  return ids;
}

export const generateDateIdKey = (date: Date, id: string) => {
  return `${id}_${date.toISOString()}`;
};
